import { NextApiRequest } from 'next';
import { URL } from 'url';
import type * as http from 'http';
import type { BrandLinks, Frame, Pages, Redirect } from '~/lib/data-contract';
import { Translations } from '~/shared/utils/translation';
import ipRangeCheck from 'ip-range-check';

type Fetch<T> = Omit<Response, 'json'> & {
    json: () => Promise<T>;
};

type ParamType = {
    [key: string]: string | string[] | undefined;
};

const { API_URL } = process.env;

/**
 * Helper method for fetching content from the API
 *
 * @param path The API path without endpoint
 * @param params Additional params for the url
 * @param headers Headers to append
 */
async function fetchAPI<T>(
    path: string,
    params: ParamType = {},
    headers: HeadersInit = {},
): Promise<Fetch<T>> {
    // Build url
    const url = new URL(path, API_URL);

    // Add query params
    Object.entries(params).forEach(([key, value]) => {
        url.searchParams.append(key, String(value));
    });

    console.info('Querying', url.href, headers);

    const response = await fetch(url.href, {
        method: 'GET',
        headers: {
            ...headers,
            'Content-Type': 'application/json; charset=utf-8',
        },
        keepalive: true,
    });

    const { status, statusText } = response;

    if (status >= 500) {
        console.error(`Failed to fetch API with url:${url}`);
        console.error(statusText);
    }

    return response;
}

/**
 * Helper method for getting the data from a frame
 *
 * @see fetchFrame
 */
export async function getFrameData(url: string, params = {}) {
    const response = await fetchFrame(url, params);

    if (!response.ok) throw new Error(`Failed to fetch frame (${response.status})`);

    return response.json();
}

/**
 * Helper method for requesting a frame from the api.
 * Returns headers, status and data
 *
 * @param url The URL for the frame
 * @param params Additional params for the request. Note these params are not added to the provided URL but to the API requst
 */
export async function fetchFrame(url: string, params = {}) {
    return fetchAPI<Frame>(`api/content/frame`, {
        url,
        ...params,
    });
}

/**
 * Helper method for requesting a page from the api.
 * Returns headers, status and data
 *
 * @param url The URL for the page
 * @param params Additional params for the request. Note these params are not added to the provided URL but to the API requst
 * @param headers Headers to append
 */
export async function fetchPage(url: string, params = {}, headers: HeadersInit = {}) {
    return fetchAPI<Pages | Redirect>(
        `api/content/page`,
        {
            url,
            ...params,
        },
        headers,
    );
}

export async function fetchBrands(params = {}, headers: HeadersInit = {}) {
    return fetchAPI<BrandLinks>(
        `api/brands/list`,
        {
            ...params,
        },
        headers,
    );
}

export async function fetchRedirect(url: string) {
    const callUrl = `${process.env.REDIRECTS_API}/integrations/Redirects/Lookup?url=${url}`;
    return fetch(callUrl, {
        headers: {
            [process.env.API_SECURITY_KEY as string]: process.env.API_SECURITY_VALUE as string,
        },
    });
}

/**
 * Helper method for getting the data from a page
 *
 * @see fetchPage
 */
export async function getPageData(url: string, params = {}) {
    const response = await fetchPage(url, params);
    return response.json();
}

/**
 * Helper method for requesting translations for a particular culture
 *
 * @param culture language, determined by CMS
 * @returns data in the type of Translations
 */
export async function getTranslationData(url = '/') {
    const response = await fetchAPI<Translations>(`api/content/translations?url=${url}`);

    if (!response.ok) throw new Error(`Failed to fetch translations (${response.status})`);

    return response.json();
}

/**
 * Helper method for setting security headers for API Management
 * @param proxyReq proxy request
 */
export const setApiSecurityHeader = (proxyReq: http.ClientRequest) => {
    if (process.env.API_SECURITY_KEY && process.env.API_SECURITY_VALUE) {
        proxyReq.setHeader(process.env.API_SECURITY_KEY, process.env.API_SECURITY_VALUE);
    }
};

export const sleep = (ms: number) => {
    return new Promise((resolve) => setTimeout(resolve, ms));
};

export const isRequestFromSameDomain = (req: NextApiRequest) => {
    const publicUrl = new URL(process.env.NEXT_PUBLIC_URL!);

    const referrerOrigin = req.headers.referer && new URL(req.headers.referer).origin;
    const origin = req.headers.origin || referrerOrigin;

    /**
     * Preprod uses Prod environment variables. If the public URL is for Prod and the origin is Preprod,
     * we will let the request through.
     */
    const isPreprod =
        publicUrl.origin === 'https://www.imerco.dk' && origin === 'https://preprod01.imerco.dk';

    return origin === publicUrl.origin || isPreprod;
};

export const extractIPFromRequest = (req: NextApiRequest) => {
    const forwarded = req.headers['x-forwarded-for'];
    const ip = typeof forwarded === 'string' ? forwarded.split(/, /)[0] : req.socket.remoteAddress;

    return ip;
};

export const isRequestFromAltaPay = (req: NextApiRequest) => {
    const ip = extractIPFromRequest(req);

    // It shouldn't be possible to not have IP on the request.
    if (!ip) return false;

    const altaPayIps = [
        '91.199.134.160',
        '91.199.134.161',
        '91.199.134.162',
        '91.199.134.163',
        '91.199.134.164',
        '91.199.134.165',
        '91.199.134.166',
        '91.199.134.167',
        '185.206.120.0/24',
        '185.203.232.129',
        '185.203.233.129',
        '2a10:a200::/29',
    ];

    // Check if the IP of the request belongs to AltaPay.
    return ipRangeCheck(ip, altaPayIps);
};
